import { AfterViewInit, Component, ElementRef, Input, NgZone, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { A11yModule } from '@angular/cdk/a11y';
import { merge, Subscription } from 'rxjs';
import { AiFormAdapter, AiFormElement, AiFormState } from '../../cyn/fck-chat-module/core/forms/ai-form.types';
import { isAiFormPage } from '../../cyn/fck-chat-module/core/forms/ai-form-values';
import { GOODBASE_FORM } from '../../cyn/fck-chat-module/core/forms/ai-form-catalog';
import {
    SelectFieldController, StaticDataCollectionController, TextFieldController,
    UiButtonComponent, UiButtonIconComponent, UiTableFieldComponent,
} from '@cynteka/ui-kit';

export interface GoodBaseOption { id: number; name: string; }
export interface GoodBaseBatchData {
    categories: GoodBaseOption[];
    units: GoodBaseOption[];
    creator: { id: number };
    initialRows: unknown[];
    addInitialRow: boolean;
}
export interface GoodBasePositionInput {
    name: string;
    category: GoodBaseOption;
    unit: GoodBaseOption;
    creator: { id: number };
    creationDate: Date;
}
interface GoodBaseRow {
    rowId: string;
    name: TextFieldController;
    category: SelectFieldController<GoodBaseOption>;
    unit: SelectFieldController<GoodBaseOption>;
    deleted: boolean;
    savedId?: number;
    saveError?: string;
}

@Component({
    selector: 'goodbase-batch-dialog',
    standalone: true,
    imports: [CommonModule, A11yModule, UiButtonComponent, UiButtonIconComponent, UiTableFieldComponent],
    templateUrl: './goodbase-batch-dialog.component.html',
    styleUrls: ['./goodbase-batch-dialog.component.scss'],
    host: { 'data-goodbase-form': 'batch', 'data-ai-form': GOODBASE_FORM.id },
})
export class GoodBaseBatchDialogComponent implements OnInit, AfterViewInit, OnDestroy {
    @Input() data!: GoodBaseBatchData;
    @Input() savePosition!: (position: GoodBasePositionInput) => Promise<{ id: number }>;
    @Input() close!: () => void;
    @Input() onSaved!: () => void;
    readonly maxRows = 100;
    rows: GoodBaseRow[] = [];
    saving = false;
    submitted = false;
    error: string | null = null;
    private nextRowId = 0;
    private destroyed = false;
    private readonly changes = new Subscription();
    private readonly toggleDropdown = (event: MouseEvent): void => {
        const target = event.target instanceof Element ? event.target : null;
        const field = target?.closest('table-field-select');
        if (!field || field.hasAttribute('disabled') || !field.querySelector('search-query-filter')
            || target?.closest('button[iconname="cross"]')) return;
        event.stopImmediatePropagation();
        // shortcut: table-field не предоставляет toggle списка; заменить при появлении публичного API в UI-kit.
        field.dispatchEvent(new KeyboardEvent('keydown', { key: 'Escape' }));
    };
    readonly aiAdapter: AiFormAdapter = {
        form: GOODBASE_FORM.id,
        read: (): AiFormState => {
            const state = this.read();
            return {
                ...state, entityId: null, readOnly: this.destroyed || !isAiFormPage(GOODBASE_FORM),
                editableFields: this.saving || this.destroyed || !isAiFormPage(GOODBASE_FORM) ? [] : ['rows'],
                errors: [...state.errors.map(error => `rows.${error.rowId}.${error.field}: ${error.message}`), ...(state.error ? [state.error] : [])],
                values: { rows: state.values.rows.map((row, index) => ({
                    ...row, category: this.rows[index].category.value, unit: this.rows[index].unit.value,
                })) },
            };
        },
        patch: async values => {
            if (!isAiFormPage(GOODBASE_FORM)) throw new Error('Форма доступна только на странице товарной базы');
            if (Object.keys(values).some(key => key !== 'rows') || !Array.isArray(values['rows'])) throw new Error('Ожидается массив rows');
            await this.patch({ rows: values['rows'].map(item => {
                const row = { ...item };
                if ('name' in row) row.name ??= '';
                for (const [key, id] of [['category', 'categoryId'], ['unit', 'unitId']]) {
                    if (key in row) { row[id] = row[key]?.id ?? null; delete row[key]; }
                }
                return row;
            }) });
        },
    };

    constructor(private readonly element: ElementRef<HTMLElement>, private readonly zone: NgZone) { }

    ngOnInit(): void {
        try {
            if (this.data.initialRows.length) this.applyPatch({ rows: this.data.initialRows });
            else if (this.data.addInitialRow) this.addRow();
        } catch (error) { this.error = error.message; }
    }

    ngAfterViewInit(): void {
        (this.element.nativeElement as AiFormElement).aiFormAdapter = this.aiAdapter;
        this.element.nativeElement.addEventListener('click', this.toggleDropdown, true);
        this.element.nativeElement.dispatchEvent(new Event('cynteka-ai-form-ready', { bubbles: true }));
    }

    ngOnDestroy(): void {
        this.destroyed = true;
        this.changes.unsubscribe();
        this.element.nativeElement.removeEventListener('click', this.toggleDropdown, true);
        delete (this.element.nativeElement as AiFormElement).aiFormAdapter;
    }

    private notifyChange(): void {
        this.element.nativeElement.dispatchEvent(new Event('cynteka-ai-form-change', { bubbles: true }));
    }

    activeCount(): number { return this.rows.filter(row => !row.deleted && !row.savedId).length; }
    savedCount(): number { return this.rows.filter(row => row.savedId).length; }
    trackRow(_index: number, row: GoodBaseRow): string { return row.rowId; }

    private rowErrors(row: GoodBaseRow) {
        if (row.deleted || row.savedId) return [];
        const errors: { rowId: string; field: string; message: string }[] = [];
        if (!row.name.value?.trim()) errors.push({ rowId: row.rowId, field: 'name', message: 'Не заполнено поле «Название»' });
        else if (row.name.value.length > 255) errors.push({ rowId: row.rowId, field: 'name', message: 'Не более 255 символов' });
        if (!row.category.value) errors.push({ rowId: row.rowId, field: 'categoryId', message: 'Не заполнено поле «Категория»' });
        if (!row.unit.value) errors.push({ rowId: row.rowId, field: 'unitId', message: 'Не заполнено поле «Единица измерения»' });
        return errors;
    }

    fieldError(row: GoodBaseRow, field: string): string | undefined {
        return this.submitted ? this.rowErrors(row).find(error => error.field === field)?.message : undefined;
    }

    private selectField(options: GoodBaseOption[], label: string): SelectFieldController<GoodBaseOption> {
        return new SelectFieldController({
            disabled: false,
            collectionConfig: {
                collectionController: new StaticDataCollectionController<GoodBaseOption, any>({
                    allCollection: options,
                    filterCallback: (filter, items) => items.filter(item => item.name.toLocaleLowerCase().includes((filter?.search ?? '').toLocaleLowerCase())),
                }),
                collectionFilters: { search: new TextFieldController() },
                searchQueryFieldName: 'search',
                allowEmpty: true,
            },
            showConfig: { label, placeholder: label, tabsLabelFactory: item => item.name, listItemLabelFactory: item => item.name },
        });
    }

    private newRow(): GoodBaseRow {
        const row: GoodBaseRow = {
            rowId: `row-${++this.nextRowId}`, deleted: false,
            name: new TextFieldController({
                showConfig: { size: 'small', placeholder: 'Наименование', disableClearButton: true },
                mask: input => ({
                    value: input.value.slice(0, 255), showValue: input.value.slice(0, 255),
                    selectionStart: Math.min(input.selectionStart ?? 255, 255), selectionEnd: Math.min(input.selectionEnd ?? 255, 255)
                }),
            }),
            category: this.selectField(this.data.categories, 'Категория'),
            unit: this.selectField(this.data.units, 'ЕИ'),
        };
        this.changes.add(merge(row.name.valueChanges, row.category.valueChanges, row.unit.valueChanges)
            .subscribe(() => this.notifyChange()));
        [row.name, row.category, row.unit].forEach((controller, index) => {
            controller.addValidator(() => {
                const message = this.fieldError(row, ['name', 'categoryId', 'unitId'][index]);
                return message ? { status: 'invalid' as const, message } : null;
            });
        });
        return row;
    }

    addRow(): void {
        if (this.saving || this.rows.length >= this.maxRows) return;
        this.rows.push(this.newRow());
        this.error = null;
        this.notifyChange();
    }

    toggleDeleted(row: GoodBaseRow): void {
        if (this.saving || row.savedId) return;
        row.deleted = !row.deleted;
        this.syncDisabled();
        this.error = null;
    }

    private syncDisabled(): void {
        for (const row of this.rows) {
            for (const controller of [row.name, row.category, row.unit]) {
                controller.disabled = this.saving || row.deleted || !!row.savedId;
                controller.validate();
            }
        }
        this.notifyChange();
    }

    read() {
        const errors = this.rows.flatMap(row => this.rowErrors(row));
        return {
            ready: !this.destroyed, busy: this.saving,
            valid: !this.destroyed && !this.saving && this.activeCount() > 0 && !errors.length,
            errors, error: this.error,
            values: {
                rows: this.rows.map(row => ({
                    rowId: row.rowId, name: row.name.value,
                    categoryId: row.category.value ? String(row.category.value.id) : null,
                    unitId: row.unit.value ? String(row.unit.value.id) : null,
                    deleted: row.deleted, savedId: row.savedId ?? null, saveError: row.saveError ?? null,
                }))
            },
            options: { categories: this.data.categories.map(item => ({ ...item })), units: this.data.units.map(item => ({ ...item })) },
        };
    }

    async patch(values: unknown) {
        this.zone.run(() => this.applyPatch(values));
        return this.read();
    }

    // rowId адресует существующую строку; без rowId строка добавляется в конец.
    private applyPatch(values: any): void {
        if (this.destroyed || this.saving) throw new Error('Форма недоступна для редактирования');
        if (!values || !Array.isArray(values.rows)) throw new Error('Ожидается объект с массивом rows');
        const ids = new Set<string>();
        const changes = values.rows.map(item => {
            if (!item || typeof item !== 'object' || Array.isArray(item)
                || Object.keys(item).some(key => !['rowId', 'name', 'categoryId', 'unitId', 'deleted'].includes(key))) throw new Error('Неизвестное поле строки');
            const row = item.rowId == null ? null : this.rows.find(row => row.rowId === item.rowId);
            if (item.rowId != null && (!row || ids.has(item.rowId))) throw new Error('Неизвестный или повторный rowId');
            if (row?.savedId) throw new Error('Сохранённую строку нельзя изменить');
            if (row) ids.add(item.rowId);
            if ('name' in item && (typeof item.name !== 'string' || item.name.length > 255)) throw new Error('Название: строка до 255 символов');
            if ('deleted' in item && typeof item.deleted !== 'boolean') throw new Error('deleted должен быть boolean');
            const selected: { category?: GoodBaseOption; unit?: GoodBaseOption } = {};
            for (const [field, key, options] of [
                ['categoryId', 'category', this.data.categories], ['unitId', 'unit', this.data.units],
            ] as const) {
                if (!(field in item)) continue;
                const option = item[field] == null ? undefined : options.find(option => String(option.id) === String(item[field]));
                if (item[field] != null && !option) throw new Error(`Недоступное значение ${field}`);
                selected[key] = option;
            }
            return { row, item, selected };
        });
        if (this.rows.length + changes.filter(change => !change.row).length > this.maxRows) throw new Error('Не более 100 строк');
        for (const change of changes) {
            const row = change.row ?? this.newRow();
            if (!change.row) this.rows.push(row);
            if ('name' in change.item) row.name.setValue(change.item.name);
            if ('deleted' in change.item) row.deleted = change.item.deleted;
            if ('category' in change.selected) row.category.setValue(change.selected.category);
            if ('unit' in change.selected) row.unit.setValue(change.selected.unit);
        }
        this.syncDisabled();
        this.error = null;
    }

    cancel(): void { if (!this.saving) this.close(); }

    async save(): Promise<void> {
        if (this.saving || this.destroyed) return;
        this.submitted = true;
        this.syncDisabled();
        if (!this.activeCount()) { this.error = 'Добавьте хотя бы один товар'; return; }
        if (this.rows.some(row => this.rowErrors(row).length)) return;
        this.saving = true;
        this.syncDisabled();
        this.error = null;
        const savedBefore = this.savedCount();
        let currentRow: GoodBaseRow | undefined;
        try {
            for (const row of this.rows.filter(row => !row.deleted && !row.savedId)) {
                if (this.destroyed) return;
                currentRow = row;
                row.saveError = undefined;
                const result = await this.savePosition({
                    name: row.name.value, category: row.category.value!, unit: row.unit.value!,
                    creator: this.data.creator, creationDate: new Date()
                });
                if (!result?.id) throw new Error('Нет подтверждения сохранения');
                row.savedId = result.id;
            }
            if (!this.destroyed) this.close();
        } catch (error) {
            if (!this.destroyed && currentRow) {
                currentRow.saveError = 'Не удалось подтвердить сохранение';
                this.error = `Сохранено: ${this.savedCount()}. Отправка остановлена на строке ${this.rows.indexOf(currentRow) + 1}. `
                    + 'Проверьте товарную базу перед повторной отправкой: при потере ответа товар мог сохраниться.';
            }
        } finally {
            this.saving = false;
            if (!this.destroyed) this.syncDisabled();
            if (this.savedCount() > savedBefore) this.onSaved();
        }
    }
}
